import { Fragment, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type LicenceRow } from '../lib/api'
import { useSession } from '../lib/session'
import { Banner } from '../components/Page'
import { SeatEvictedCard, SeatExpiringCard, StateKind } from '../components/SeatNotice'
import {
  useExpiryCountdown,
  useMySeatState,
  type MySeatSession,
} from '../components/reader/useSeat'

/**
 * Board 05.6: account and access status.
 *
 * Everything on this card is a fact about this reader's access, and each one
 * is read back from the API rather than assumed: the institution and role from
 * `/auth/me`, the licence, seat limit and session timeout from
 * `/licences/mine`, the rights from the licence's own permits flags crossed
 * with the caller's permissions.
 *
 * The seat and session lines come from `GET /content-sessions/mine`, which
 * reports without acquiring. That distinction matters: asking the acquire
 * endpoint what the seat is doing would take the seat in order to answer, and
 * on a one-seat licence that is exactly the wrong thing for a status screen to
 * do. The countdown is the server's own `expires_at`, not a local guess.
 *
 * One line still has no API behind it and says so rather than showing a
 * plausible number: the API call allowance.
 */
export function AccountPage() {
  const { me, config, can } = useSession()
  const [panel, setPanel] = useState<'keys' | 'downloads' | null>(null)

  const licences = useQuery({
    queryKey: ['licences', 'mine'],
    queryFn: () => api.get<LicenceRow[]>('/licences/mine'),
    retry: false,
  })

  const seat = useMySeatState(!!me)
  const live: MySeatSession | null = seat.state?.sessions?.[0] ?? null
  const remaining = useExpiryCountdown(live?.expires_at)
  const lastEnded = seat.state?.last_ended ?? null
  const [dismissedEviction, setDismissedEviction] = useState(false)

  if (!me) return null

  const rows: [string, ReactNode][] = []
  rows.push(['institution', me.institution?.name ?? 'none recorded'])
  rows.push([
    'role',
    me.roles.length ? me.roles.join(' · ') : 'no role granted',
  ])

  const held = licences.data ?? []
  if (held.length === 0) {
    rows.push([
      'licence',
      licences.isLoading ? 'reading' : `no licence held by ${me.institution?.name ?? 'this account'}`,
    ])
    rows.push(['users', '-'])
    rows.push(['session', '-'])
  } else {
    for (const licence of held) {
      rows.push([
        held.length > 1 ? `licence · ${licence.publication_name ?? 'untitled'}` : 'licence',
        [
          licence.publication_name ?? 'untitled publication',
          scope(licence),
          licence.is_trial ? 'trial' : null,
          licence.active ? null : 'inactive',
        ]
          .filter(Boolean)
          .join(' · '),
      ])
    }
    const first = held[0]
    const mine = seat.state?.sessions?.find((row) => row.licence_id === first.id) ?? null
    const waiting = seat.state?.queue?.find((row) => row.licence_id === first.id) ?? null
    rows.push([
      'users',
      [
        seats(first),
        mine ? 'in use by you' : waiting ? `you are number ${waiting.position} in the queue` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    ])
    rows.push(['session', session(first, remaining, !!mine)])
  }

  rows.push(['rights', rights(held[0] ?? null, can)])
  rows.push([
    'api',
    can('content.api')
      ? 'enabled on this account · no published allowance'
      : 'not enabled on this account',
  ])

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-6 md:px-6">
      <div className="mb-3 flex items-baseline gap-2.5">
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-accent">5.6</div>
        <h1 className="chrome">Account &amp; access status</h1>
      </div>

      {/*
        The seat is the scarcest thing in the product on a one-seat licence, so
        its two moments are shown here rather than buried: a session about to
        expire, and a session library staff ended.
      */}
      {live && remaining !== null && remaining <= 120 && (
        <div className="mb-4">
          <StateKind>Your own session, about to expire</StateKind>
          <SeatExpiringCard
            remainingS={remaining}
            someoneWaiting={live.queue_depth > 0}
            onStay={() => {
              void api.post(`/content-sessions/${live.licence_id}/heartbeat`).then(seat.refresh)
            }}
            onRelease={() => {
              void api.post(`/content-sessions/${live.licence_id}/release`).then(seat.refresh)
            }}
          />
        </div>
      )}

      {!live && lastEnded?.reason === 'evicted' && !dismissedEviction && (
        <div className="mb-4">
          <StateKind>Evicted by an administrator</StateKind>
          <SeatEvictedCard
            reason={lastEnded.note}
            at={lastEnded.ended_at ? new Date(lastEnded.ended_at).toLocaleString() : null}
            by={lastEnded.ended_by}
            onRejoin={() => setDismissedEviction(true)}
          />
        </div>
      )}

      <section className="rounded-[--radius-md] border border-rule2 bg-paper px-[18px] py-4">
        <div className="mb-3.5 flex items-baseline gap-2.5">
          <h2 className="text-[20px] font-semibold leading-[1.15]">{me.display_name}</h2>
          <div className="font-mono text-[10px] uppercase text-ink2">
            {me.roles[0] ?? 'reader'}
          </div>
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-[7px] font-mono text-[12px] leading-[1.5]">
          {rows.map(([label, value]) => (
            <Fragment key={label}>
              <dt className="text-ink2">{label}</dt>
              <dd className="m-0 text-body">{value}</dd>
            </Fragment>
          ))}
        </dl>

        <div className="mt-3.5 flex gap-1.5 border-t border-n300 pt-3">
          <button
            type="button"
            className="btn-secondary px-3 py-1.5 text-[13.5px] font-normal"
            aria-expanded={panel === 'keys'}
            onClick={() => setPanel(panel === 'keys' ? null : 'keys')}
          >
            API keys
          </button>
          <button
            type="button"
            className="btn-secondary px-3 py-1.5 text-[13.5px] font-normal"
            aria-expanded={panel === 'downloads'}
            onClick={() => setPanel(panel === 'downloads' ? null : 'downloads')}
          >
            Download history
          </button>
        </div>

        {panel === 'keys' && (
          <Banner tone="neutral" className="mt-3">
            {can('content.api')
              ? 'Your account carries API access. Self-service key issue and revocation is specified and not built; your librarian issues a key today.'
              : 'API access is not on this account. Your librarian can add it.'}
          </Banner>
        )}
        {panel === 'downloads' && (
          <Banner tone="neutral" className="mt-3">
            Every content access is audited, and your library can produce your history from the
            usage report. A reader-facing view of that log is specified and not built.
          </Banner>
        )}
      </section>

      <p className="mt-3 font-mono text-[9.5px] uppercase leading-[1.7] text-ink2">
        {config?.support_email
          ? `Access questions go to ${config.support_email}.`
          : 'Access is governed by the licence your institution holds with the publisher.'}
      </p>
    </div>
  )
}

/** The years a licence covers, which is what the denial screen also reads. */
function scope(licence: LicenceRow): string | null {
  if (!licence.starts_on && !licence.ends_on) return null
  const from = licence.starts_on?.slice(0, 4) ?? '?'
  const to = licence.ends_on?.slice(0, 4) ?? 'open'
  return `${from}–${to}`
}

function seats(licence: LicenceRow): string {
  if (licence.access_model === 'unlimited') return 'unlimited'
  if (licence.access_model === 'named_seat') {
    return licence.named_seat_limit === null
      ? 'named users'
      : `${licence.named_seat_limit} named user${licence.named_seat_limit === 1 ? '' : 's'}`
  }
  return licence.concurrent_limit === null
    ? 'concurrent'
    : `${licence.concurrent_limit} concurrent`
}

/**
 * The session line. With a seat open it counts down against the server's own
 * `expires_at`; with none open it describes what a session would be, which is
 * the licence's configuration rather than a countdown of nothing.
 */
function session(
  licence: LicenceRow,
  remaining: number | null,
  open: boolean,
): string {
  const idle = licence.session_idle_timeout_s
  const max = licence.session_max_s
  if (open && remaining !== null) {
    const minutes = Math.floor(remaining / 60)
    return [
      minutes >= 1 ? `${minutes} min remaining` : `${remaining} s remaining`,
      'extends on activity',
    ].join(' · ')
  }
  const bits = [
    'no session open',
    idle ? `ends after ${Math.round(idle / 60)} min idle` : null,
    max ? `${Math.round(max / 3600)} h maximum` : null,
  ].filter(Boolean)
  return bits.join(' · ')
}

/**
 * What this reader may do with what they read. The licence decides what the
 * institution bought; the permission decides whether this person has it. Both
 * have to be true, so both are read.
 */
function rights(licence: LicenceRow | null, can: (permission: string) => boolean): string {
  const bits = [
    `${licence?.permits_download && can('content.download') ? '' : 'no '}download`,
    `${licence?.permits_export && can('content.export_bulk') ? '' : 'no '}export`,
    `${can('content.tdm') ? '' : 'no '}TDM`,
  ]
  return bits.join(' · ')
}
